import { Effect, type Scope } from 'effect';
import { attemptStudio, attemptStudioPromise, type StudioRuntimeError } from '../effect-runtime';

/** Keep the request signal and unread body owned until the consumer finishes. */
export const studioHttpEffect = Effect.fn('studio.http')(function* <A>(
  url: string,
  init: RequestInit,
  use: (response: Response) => Effect.Effect<A, StudioRuntimeError>,
  request: typeof fetch = fetch,
): Effect.fn.Return<A, StudioRuntimeError, Scope.Scope> {
  const owner = yield* Effect.acquireRelease(
    Effect.sync(() => ({
      controller: new AbortController(),
      response: undefined as Response | undefined,
    })),
    (owner) =>
      Effect.gen(function* () {
        owner.controller.abort();
        const body = owner.response?.body;
        if (body && !body.locked) {
          yield* attemptStudioPromise('api', 'response-discard', () => body.cancel()).pipe(
            Effect.ignore,
          );
        }
      }),
  );
  const signal = init.signal
    ? AbortSignal.any([owner.controller.signal, init.signal])
    : owner.controller.signal;
  const response = yield* attemptStudioPromise('api', 'http-request', async () => {
    const response = await request(url, { ...init, signal });
    // Some transports ignore abort. Discard late ownership instead of adopting it.
    if (signal.aborted) {
      await response.body?.cancel().catch(() => undefined);
      throw new Error('Response arrived after cancellation');
    }
    owner.response = response;
    return response;
  });
  return yield* use(response);
}, Effect.scoped);

export const readStudioJsonEffect = Effect.fn('studio.readJson')(function* (
  response: Response,
): Effect.fn.Return<unknown, StudioRuntimeError, Scope.Scope> {
  const reader = response.body
    ? yield* Effect.acquireRelease(
        attemptStudio('api', 'response-reader', () => response.body!.getReader()),
        (reader) =>
          attemptStudioPromise('api', 'response-reader-cancel', () => reader.cancel()).pipe(
            Effect.ignore,
            Effect.ensuring(
              attemptStudio('api', 'response-reader-release', () => reader.releaseLock()).pipe(
                Effect.ignore,
              ),
            ),
          ),
      )
    : undefined;
  const chunks: Uint8Array[] = [];
  let length = 0;
  if (reader) {
    while (true) {
      const chunk = yield* attemptStudioPromise('api', 'response-read', () => reader.read());
      if (chunk.done) break;
      chunks.push(chunk.value);
      length += chunk.value.length;
    }
  }
  return yield* attemptStudio('api', 'response-json', () => {
    const bytes = new Uint8Array(length);
    let offset = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.length;
    }
    return JSON.parse(new TextDecoder().decode(bytes));
  });
}, Effect.scoped);
